<template>
  <div ref="contents" class="c-focus-loop">
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { tabbable } from 'tabbable';
import type { FocusableElement } from 'tabbable';

const contents = ref<HTMLElement>();

const tabbableNodes = ref([]);

let observer: MutationObserver;
let eventTarget: Document | ShadowRoot;

const firstTabbable = computed(() => tabbableNodes.value[0]);
const lastTabbable = computed(() => tabbableNodes.value[tabbableNodes.value.length - 1]);

const getActiveElement = (): Element | null => {
  let activeEl = document.activeElement;

  // Traverse through shadow roots to find the actual active element
  while (activeEl?.shadowRoot?.activeElement) {
    activeEl = activeEl.shadowRoot.activeElement;
  }

  return activeEl;
};

const tabbableFocused = (element: FocusableElement) => element === getActiveElement();

const focusInListener = (event: KeyboardEvent) => {
  if (!contents.value) return;

  const activeElement = getActiveElement();

  if (!contents.value.contains(activeElement)) {
    firstTabbable.value.focus();
    return;
  }

  const tabPressed = event.key === 'Tab';
  const shiftTabPressed = tabPressed && event.shiftKey;

  if (tabbableFocused(firstTabbable.value) && shiftTabPressed) {
    event.preventDefault();
    lastTabbable.value.focus();
  } else if (tabbableFocused(lastTabbable.value) && tabPressed && !shiftTabPressed) {
    event.preventDefault();
    firstTabbable.value.focus();
  }
};

onMounted(() => {
  tabbableNodes.value = tabbable(contents.value, {
    getShadowRoot: true,
  });
  observer = new MutationObserver(() => {
    tabbableNodes.value = tabbable(contents.value, {
      getShadowRoot: true,
    });
  });

  observer.observe(contents.value, {
    childList: true,
    subtree: true,
  });

  // Resolved once and kept, so unmount always detaches from the node we
  // attached to — by then the element may already be out of the document.
  const rootElement = contents.value?.getRootNode() as Document | ShadowRoot;
  eventTarget = rootElement || document;

  eventTarget.addEventListener('keydown', focusInListener);
  firstTabbable.value?.focus();
});

onBeforeUnmount(() => {
  observer.disconnect();
  eventTarget?.removeEventListener('keydown', focusInListener);
});
</script>
